import React from "react";
import { useQuantityIndex } from "../../../../../../api/useQuantities";
import t from "../../../../../../i18n";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import type { Failure } from "../../../../../../api/useSetups";
import Table from "@ui/Table";
import createGlobalState from "@ui/lib/createGlobalState";
import type { SortableValue } from "diagnostics-lib/comparator";
import useSortedData from "../../../../../../lib/hooks/useSortedData";
import createIOSortType from "../../../../../../lib/createIOSortType";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { InformationLine } from "@ui/IconV2/react";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";

//#region
const rule = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
`;
const ruleInformationLine = css`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ruleTooltip = css`
  display: flex;
  width: 350px;
  padding: ${spacings.m};
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacings.xs};
`;
//#endregion

enum FilterProperties {
  RULE = "rule",
  FAULT = "fault",
  QUANTITY = "quantity",
  MINIMUN = "min",
  MAXIMUM = "max",
  UNIT = "unit",
}
const faultFilterColumns = [
  FilterProperties.RULE,
  FilterProperties.FAULT,
  FilterProperties.QUANTITY,
  FilterProperties.MINIMUN,
  FilterProperties.MAXIMUM,
] as const;
type FaultFilterColumns = (typeof faultFilterColumns)[number];
type FaultFilter = {
  rule: string;
  fault: string;
  quantity: string;
  min: number | null;
  max: number | null;
  unit: string | null;
};

const useFautFilterSort = createGlobalState({
  storage: "session",
  key: "faultFilterSortTable",
  type: createIOSortType(faultFilterColumns),
  defaultValue: {
    column: FilterProperties.QUANTITY,
    direction: "asc",
  },
  scoped: true,
});

const formatValueUnit = (value: number | null, unit: string | null) => {
  if (value !== null && unit !== null) {
    return `${value} ${unit}`;
  }

  return value;
};

const getLabel = (column: FaultFilterColumns) => {
  switch (column) {
    case FilterProperties.RULE:
      return (
        <div className={rule}>
          {t("setup.failure.table.failure_filter.definitions.column.rule")}
          <TooltipV2
            className={ruleTooltip}
            placement="top"
            overlay={
              <OverlayWrapper
                text={t("setup.failure.table.failure_filter.definitions.column.rule.tooltip")}
              />
            }
          >
            <span className={ruleInformationLine}>
              <InformationLine />
            </span>
          </TooltipV2>
        </div>
      );
    case FilterProperties.FAULT:
      return t("setup.failure.table.failure_filter.definitions.column.failure");
    case FilterProperties.QUANTITY:
      return t("setup.failure.table.failure_filter.definitions.column.quantity");
    case FilterProperties.MINIMUN:
      return t("setup.failure.table.failure_filter.definitions.column.minimum");
    case FilterProperties.MAXIMUM:
      return t("setup.failure.table.failure_filter.definitions.column.maximum");
    default:
      throw new Error(column satisfies never);
  }
};

const getRule = (column: "CONSTRAINT" | "PROMOTION") => {
  switch (column) {
    case "CONSTRAINT":
      return t("setup.failure.table.failure_filter.definitions.value.rule.constraint");
    case "PROMOTION":
      return t("setup.failure.table.failure_filter.definitions.value.rule.promotion");
    default:
      throw new Error(column satisfies never);
  }
};

const metadata: { [key in FaultFilterColumns]: ColumnMetaData<FaultFilter> } = {
  [FilterProperties.RULE]: {
    label: getLabel(FilterProperties.RULE),
    render: filter => getRule(filter.rule as "CONSTRAINT" | "PROMOTION"),
    sortable: true,
    minWidth: "150px",
    maxWidth: "150px",
  },
  [FilterProperties.FAULT]: {
    label: getLabel(FilterProperties.FAULT),
    render: filter => filter.fault,
    sortable: true,
    minWidth: "200px",
    maxWidth: "600px",
  },
  [FilterProperties.QUANTITY]: {
    label: getLabel(FilterProperties.QUANTITY),
    render: filter => filter.quantity,
    sortable: true,
    minWidth: "200px",
    maxWidth: "600px",
  },
  [FilterProperties.MINIMUN]: {
    label: getLabel(FilterProperties.MINIMUN),
    render: filter => formatValueUnit(filter.min, filter.unit),
    sortable: true,
    minWidth: "120px",
    maxWidth: "250px",
  },
  [FilterProperties.MAXIMUM]: {
    label: getLabel(FilterProperties.MAXIMUM),
    render: filter => formatValueUnit(filter.max, filter.unit),
    sortable: true,
    minWidth: "120px",
    maxWidth: "250px",
  },
};

const sortSelector: { [key in FaultFilterColumns]: (filter: FaultFilter) => SortableValue | null } =
  {
    [FilterProperties.RULE]: filter => filter.rule,
    [FilterProperties.FAULT]: filter => filter.fault,
    [FilterProperties.QUANTITY]: filter => filter.quantity,
    [FilterProperties.MINIMUN]: filter => filter.min,
    [FilterProperties.MAXIMUM]: filter => filter.max,
  };

const FaultFilterTable = ({ projectId, failures }: { projectId: string; failures: Failure[] }) => {
  const indexedQuantities = useQuantityIndex(projectId);
  const data = React.useMemo(() => {
    const arrayFilter: FaultFilter[] = [];
    for (const failure of failures) {
      for (const filter of failure.filters) {
        const quantity = indexedQuantities[filter.quantityId];
        if (!quantity) throw new Error(`Quantity with id ${filter.quantityId} not found`);

        arrayFilter.push({
          [FilterProperties.RULE]: filter.rule ?? "CONSTRAINT",
          [FilterProperties.FAULT]: failure.displayName,
          [FilterProperties.QUANTITY]: quantity.name,
          [FilterProperties.MINIMUN]: filter.min,
          [FilterProperties.MAXIMUM]: filter.max,
          [FilterProperties.UNIT]: quantity.unit ?? null,
        });
      }
    }
    return arrayFilter.sort((a, b) =>
      a[FilterProperties.FAULT].localeCompare(b[FilterProperties.FAULT]),
    );
  }, [failures, indexedQuantities]);
  const [sort, setSort] = useFautFilterSort({ scope: projectId });
  const sortedDataFilters = useSortedData(data, sortSelector[sort.column], sort.direction);

  return (
    <Table
      stickyHeader
      datas={sortedDataFilters}
      metadata={metadata}
      computeRowKey={(filter: FaultFilter) =>
        `filter-id-${filter.fault}-${filter.quantity}-${filter.rule}`
      }
      sort={sort}
      onSort={setSort}
    />
  );
};

export default React.memo(FaultFilterTable);
